import axios from 'axios'
import { useMemo, useRef } from 'react'
import { useQueries } from '@tanstack/react-query'
import { extractErrorMessage } from '../../utils/apitools'
import useAccount from '../useAccount'
import { keepWhileOnlyFilterChanges, labelsScope, skyProductURL, skyQueryKey } from './skyEndpoints'
import type { ChartDefinition } from '../../components/social-media/chart-types'
import type { LabelQuery } from '../../components/social-media/types'
import type { PlatinumData } from '../../types/carbon/models/PlatinumData'

export interface UseSkyChartsResult {
  /** Chart id → its table, or null while loading / on error. */
  data: Record<string, PlatinumData | null>
  isLoading: boolean
  /** The previous filter's tables are on screen while the new ones load. */
  isUpdating: boolean
  error: Error | null
  refetch: () => void
}

/** One chart's slot in a batch response. Exactly one of data/error is set. */
interface SpecBatchResult {
  id: string
  data?: PlatinumData
  error?: string
}

/**
 * chartGroupKey names the charts that can travel in ONE request: everything the
 * server needs to answer them EXCEPT the side variable.
 *
 * 🔴 EVERY FIELD THAT CHANGES THE ANSWER MUST BE IN THIS KEY. A field left out
 * merges two charts that are not asking the same question, and the result is not
 * an error — it is one chart rendered from another chart's numbers, at HTTP 200.
 * `top` is the obvious one (count and month produce different columns);
 * `weight` and `caseFilter` matter just as much, and `driver` decides which
 * ENGINE answers.
 *
 * 🔴 THE MIRRORED VECTOR FILTER IS NORMALISED AWAY, AND WITHOUT THAT THIS WHOLE
 * TICKET BUYS NOTHING. Every definition in `pages/sky-social-media/charts.ts`
 * carries `filter` equal to its OWN side axis — `side: 'sentiment(*)', filter:
 * 'sentiment(*)'`. Keyed literally, the page's eight charts would be eight
 * groups and nothing would ever merge.
 *
 * That mirror is a no-op by construction, not by luck. The Go engine ACCEPTS a
 * GlobalFilter only when it equals the side axis and then does not apply it at
 * all — facts exist only for coded records, so the predicate is already true of
 * every row (see runQuery's GlobalFilter guard, which refuses anything else and
 * falls the chart back to Carbon). So `filter === side` is dropped from the key,
 * and any OTHER filter is kept: a chart the engine would refuse must not be
 * merged with one it would serve.
 *
 * `labelQuery` is deliberately absent: it is one value for the whole hook call,
 * so it cannot differ between two charts here, and the request carries it once
 * at the top level (see below).
 */
function chartGroupKey(chart: ChartDefinition): string {
  const c = chart.carbon
  const mirroredFilter = (c.filter ?? '').trim() === (c.side ?? '').trim()
  return JSON.stringify([
    c.top ?? '',
    mirroredFilter ? '' : (c.filter ?? ''),
    c.weight ?? '',
    c.caseFilter ?? '',
    c.driver ?? '',
  ])
}

/**
 * Runs a set of crosstab charts for one Sky product.
 *
 * ─── ONE REQUEST PER (filter, top axis), NOT ONE PER CHART (T16) ────────────
 * A product page's eight charts share one filter and differ only in their side
 * variable (see `pages/sky-social-media/charts.ts`: sentiment, emotion,
 * skyrelevant and skyattributed, over the count and month axes). That was eight
 * POSTs and sixteen aggregations for what is really two questions. Charts
 * sharing a group key now travel together to `POST
 * /sky-social-media/:product/spec/run-batch`, and the server merges the scans
 * they have in common — `variable_name` is a column, so four charts become one
 * GROUPING SETS scan, and the record-bases query (which never reads the side
 * variable at all) is asked once instead of four times.
 *
 * Charts still render INDEPENDENTLY. The batch answers 200 with a per-chart
 * result, so one chart the engine cannot serve reports its own error while its
 * neighbours draw — a page must not blank because one breakdown is unavailable.
 *
 * ─── ON `labelQuery` LIVING AT THE TOP LEVEL ────────────────────────────────
 * 🔴 This is a security shape, not a convenience. The backend enforces a
 * department's campaign scope by REWRITING the top level of the request body;
 * a filter nested inside each element of `specs` would sail past that rewrite
 * and every chart would be unscoped — one department's totals under another's
 * heading, HTTP 200, nothing on screen wrong. The server refuses a per-spec
 * `labelQuery` outright, so this is not a convention that can quietly drift.
 *
 * ON `driver`. Sent once for the batch, and the backend overwrites it with the
 * same value anyway. Both belts are deliberate. Carbon answers an
 * entity-filtered spec with HTTP 200 and UNFILTERED numbers — no error, no empty
 * result — so a request that reached the server without a driver would render
 * another department's totals under this one's heading.
 */
export function useSkyCharts(
  product: string,
  charts: ChartDefinition[],
  enabled = true,
  labelQuery?: LabelQuery,
): UseSkyChartsResult {
  const account = useAccount()
  const active = Boolean(product && account.user && enabled)
  // The filter must reach the KEY as well as the body. A key that ignored it
  // would serve the unfiltered answer to a filtered view out of cache — HTTP
  // 200, plausible numbers, no error, which is the failure mode this whole
  // route group exists to make impossible.
  const labelsKey = useMemo(() => JSON.stringify(labelQuery ?? null), [labelQuery])

  // Group in DEFINITION ORDER, so the requests a page fires are stable between
  // renders and the react-query keys below are too.
  const groups = useMemo(() => {
    const byKey = new Map<string, ChartDefinition[]>()
    for (const chart of charts) {
      const key = chartGroupKey(chart)
      const bucket = byKey.get(key)
      if (bucket) bucket.push(chart)
      else byKey.set(key, [chart])
    }
    return Array.from(byKey.values())
  }, [charts])

  const queries = useQueries({
    queries: groups.map((group) => {
      // The key names every chart in the batch: two groups that happened to
      // share a key would serve each other's tables out of cache.
      const queryKey = skyQueryKey(product, 'chart-batch', {
        chartIds: group.map((c) => c.id).join(','),
        labelsKey,
      })
      return {
      queryKey,
      enabled: active,
      staleTime: 30000,
      retry: 1,
      retryDelay: 1000,
      // A filter change keeps the previous tables on screen until the new ones
      // land; a different product or chart batch never does. useQueries does
      // not pass the previous key's data here today, so `lastAnswer` below is
      // what actually does it; this stays so the two agree if it ever does.
      placeholderData: keepWhileOnlyFilterChanges<Record<string, PlatinumData | null>>(queryKey),
      queryFn: async ({ signal }: { signal: AbortSignal }): Promise<Record<string, PlatinumData | null>> => {
        try {
          const res = await axios.post<{ results: SpecBatchResult[] }>(
            skyProductURL(product, '/spec/run-batch'),
            {
              labelQuery,
              driver: 'platinum',
              specs: group.map((chart) => ({
                id: chart.id,
                top: chart.carbon.top,
                side: chart.carbon.side,
                filter: chart.carbon.filter,
                weight: chart.carbon.weight,
                caseFilter: chart.carbon.caseFilter,
              })),
            },
            // A superseded request is aborted so the browser stops waiting for
            // it. The server does NOT see the abort: Fiber's handler context
            // never ends early, so the query runs to completion in Postgres
            // (design/2026-10-16-sky-dashboard-speed.md, rollout step 4).
            { signal },
          )
          const out: Record<string, PlatinumData | null> = {}
          // Seed from the GROUP, not from the response: a chart the server
          // omitted must read as "no table", never as a missing key some caller
          // then indexes into.
          for (const chart of group) out[chart.id] = null
          for (const result of res.data?.results ?? []) {
            if (result.id in out) out[result.id] = result.data ?? null
          }
          return out
        } catch (err) {
          throw new Error(extractErrorMessage(err))
        }
      },
      }
    }),
  })

  // 🔴 useQueries DOES NOT HAND A NEW KEY THE PREVIOUS KEY'S DATA. Unlike
  // useQuery, its observers are matched by query hash, so a filter change
  // starts every batch with no placeholder and the charts blank — the exact
  // thing keepWhileOnlyFilterChanges fixes for the single-query hooks (a test
  // caught this). So the last real answer per batch is remembered here, for
  // THIS product only, and shown while the batch's new filter loads.
  //
  // A render-phase ref write, not state: it records what was rendered and must
  // not itself cause a render.
  //
  // Kept per product AND scope (the date ranges and the source, labelsScope):
  // a date range change blanks the charts like a department switch does.
  const lastAnswer = useRef(new Map<string, { view: string; data: Record<string, PlatinumData | null> }>())
  const batchIds = groups.map((group) => group.map((c) => c.id).join(','))
  const view = `${product}|${labelsScope(labelsKey)}`
  queries.forEach((q, i) => {
    if (q.data && !q.isPlaceholderData) lastAnswer.current.set(batchIds[i], { view, data: q.data })
  })
  const shown = queries.map((q, i) => {
    if (q.data) return { data: q.data, previous: q.isPlaceholderData }
    const last = lastAnswer.current.get(batchIds[i])
    if (active && q.isFetching && last && last.view === view) return { data: last.data, previous: true }
    return { data: undefined, previous: false }
  })

  const data = useMemo(() => {
    const out: Record<string, PlatinumData | null> = {}
    charts.forEach((chart) => {
      out[chart.id] = null
    })
    shown.forEach((s) => {
      Object.assign(out, s.data ?? {})
    })
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charts, product, shown.map((s, i) => `${queries[i].dataUpdatedAt}:${s.previous}:${s.data ? 1 : 0}`).join(',')])

  return {
    data,
    // True only while there is nothing to show yet: a filter change keeps the
    // previous tables, so it is `isUpdating` instead.
    isLoading: queries.some((q, i) => q.isLoading && !shown[i].data),
    isUpdating: shown.some((s) => s.previous),
    error: (queries.find((q) => q.error)?.error as Error) ?? null,
    refetch: () => queries.forEach((q) => q.refetch()),
  }
}

export default useSkyCharts
